import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useReducer,
	useRef,
} from 'react';
import _ from 'underscore';
import {assetJwtStore} from '@netx/core-asset-next/lib/assetJwt/store/assetJwtStore';
import type {AttributeAttributes} from '@netx/core-asset/models/attribute';
import {types} from '@netx/core-asset/properties/attributes';
import {useAttributeSetSelectionProvider} from '@netx/core-attribute-set-selection/context';
import attributeFormatters from '@netx/core-attribute/formatters';
import attributeParsers from '@netx/core-attribute/parsers';
import attributeParsersOut from '@netx/core-attribute/parsers-out';
import {parseValue} from '@netx/core-attribute/utils';
import attributeValidators from '@netx/core-attribute/validators';
import {useCurrentCart} from '@netx/core-cart-current';
import type {FormActions, FormValues} from '@netx/core-components/Form';
import useWhenChangedEffect from '@netx/core-components/hooks/useWhenChangedEffect';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {typedEntries} from '@netx/core-next/utils/object';
import {i18n} from '@netx/core/i18n';
import {isEmptyValue} from '@netx/core/lib/underscore-netx';
import {
	type SetAssetAttributesAttribute,
	type SetAssetAttributesSchema,
} from '../../schema';
import {type SearchAssetAttributesSchema} from '../../schema/searchAssetAttributes';
import type {
	ColumnProps,
	ColumnSchema,
	DataRow,
	GridEditor,
	GridEditorColumn,
	HypergridCellEvent,
	Permissions,
} from '../../types';
import {
	breakItUp,
	checkEquality,
	getColumnFromGrid,
	getColumnsByName,
	getColumnsFromGrid,
	getDataFromGrid,
	getDataPointFromCellEvent,
	getSortIndexesFromGrid,
} from '../../utils';
import {useChangeStore} from '../change';
import {useProgressStore} from '../progress';
import {useSelectionStore} from '../selection';
import {ACTIONS} from './constants';
import {useGetData, useSaveData} from './data';
import {loose as _getSelectedValues} from './get-values';
import reducer, {initialState} from './reducer';
import {
	all as resetAllValues,
	loose as resetSelectedValues,
} from './reset-values';
import {
	loose as updateSelectedValues,
	specific as updateSpecificValues,
} from './set-values';

type GridContextType = {
	state: typeof initialState;
	// attributeSetId: string;
	getCloneData(y: number):
		| {
				cloneData: Record<string, any>;
				cloneFields: GridEditorColumn[];
				cloneSchema: Record<string, any>[] | undefined;
				cloneTargets: Record<string, any>[];
		  }
		| undefined;
	getSelectedValues(): Promise<void>;
	resetAllChanges(): Promise<void>;
	resetSelectedChanges(): Promise<void>;
	resetStore(): void;
	save(): Promise<void>;
	setData(data: FormValues[], schema: Record<string, any>[]): void;
	updateData(values: FormValues, actions?: FormActions): Promise<void>;
};

const StoreContext = createContext<GridContextType>({} as any);

export const useGridStore = () => useContext<GridContextType>(StoreContext);

const getColumnPropsForAttribute = (attr: AttributeAttributes) => {
	const type = types[attr.type];

	const props: ColumnProps = {
		name: attr.name,
		header: attr.name,
		type,
		id: attr.attributeid,
		items: null,
		multiline: attr.type === 2,
		multivalue: attr.multivalue,
		required: attr.mandatory === 1,
		readOnly: attr.permission === 'r',
		typeId: attr.type,
		// Tags and semantics
		semanticTypeId: attr.semanticTypeId,
		spec1: attr.spec1,
		spec2: attr.spec2,

		// For editing
		localizer: {
			format: (value: any, opts: any) =>
				// @ts-ignore
				attributeFormatters[attr.type](value, attr),
			// @ts-ignore
			parse: (value: any) => attributeParsers[attr.type](value),
			invalid: (value: any) => {
				// @ts-ignore
				const valid = attributeValidators[attr.type](value);
				if (valid === true) return false;
				if (valid instanceof Error) {
					return valid.message;
				}
				return i18n.t('i18n.invalid');
			},
		},
		// @ts-ignore
		parseOutValue: (value: any) => attributeParsersOut[attr.type](value),
		// For display
		formatValue: (value: any, opts: any) =>
			// @ts-ignore
			attributeFormatters[attr.type](value, attr),
		// @ts-ignore
		parseInValue: (value: any) => attributeParsers[attr.type](value),
	};

	switch (type) {
		case 'select':
			props.items = attr.pulldownOptions;
			break;
		case 'number':
			props.type = attr.spec2 && attr.spec2 !== 4 ? 'float' : 'int';
			break;
	}

	return props;
};

const getCellEditor =
	(
		plugin: GridEditor,
		permissionsRef: React.RefObject<Permissions>,
	): GridEditor['behavior']['dataModel']['getCellEditorAt'] =>
	(x, y, declaredEditorName, cellEvent) => {
		const permissions = permissionsRef?.current || {};

		const column = getColumnFromGrid(plugin, x);
		if (column.properties.readOnly) return null;

		if (y > 0) y--;

		const data = getDataFromGrid(plugin);
		const indexes = getSortIndexesFromGrid(plugin);
		const realIndex = indexes[y];
		const rowData = data[realIndex];
		if (!rowData) return;
		const assetId = rowData.assetId;
		const perms = permissions[assetId];

		if (perms && perms.includes(column.name)) return null;

		let editorType = column.type || 'textfield';
		switch (editorType) {
			case 'int':
			case 'float':
				editorType = 'number';
				break;
			case 'date':
				editorType = 'datepicker';
				break;
			case 'text':
				editorType = 'textfield';
				break;
		}

		return plugin.cellEditors.create(
			editorType,
			Object.assign(cellEvent, {
				items: column.properties.items,
			}),
		);
	};

interface GridEditorProviderProps {
	onSetData: (data: DataRow[], schema: ColumnSchema) => void;
	gridEditor?: GridEditor;
}

export const GridStoreProvider = ({
	children,
	onSetData,
	gridEditor,
}: React.PropsWithChildren<GridEditorProviderProps>) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	const getData = useGetData();
	const saveData = useSaveData();
	const {cart} = useCurrentCart();

	const dataRef = useRef<DataRow[]>([]);
	const rawDataRef = useRef<SearchAssetAttributesSchema[]>([]);
	const schemaRef = useRef<ColumnSchema>();
	const permissionRef = useRef<Permissions>({});

	const progressStore = useProgressStore();

	const changeStore = useChangeStore();
	const changeStoreRef = useRef(changeStore);
	useEffect(() => {
		changeStoreRef.current = changeStore;
		gridEditor?.repaint();
	}, [changeStore, gridEditor]);

	const selectionStore = useSelectionStore();
	const selectionStoreRef = useRef(selectionStore);
	useEffect(() => {
		selectionStoreRef.current = selectionStore;
	}, [selectionStore]);

	const attributeSetSelection = useAttributeSetSelectionProvider();

	const getGridData = useCallback(
		async (data: SearchAssetAttributesSchema[]) => {
			const size = data.length;
			const gridData: DataRow[] = [];
			// indexed by asset id- will contain an array of read only field names
			const permissions: Permissions = {};
			const attributeTemplates = Object.values(
				attributeSetSelection.attributeTemplates,
			).flat();

			return breakItUp(
				{
					size,
					amount: 10,
				},
				(index) => {
					progressStore.update(Math.round(((index + 1) / size) * 100));

					const datum = data[index];
					const {assetId, readOnly} = datum;
					const rowData = {
						assetId,
						jwt: datum.jwt,
						name: datum.name,
						thumbUrl: datum.thumbUrl,
					};

					datum.attributes.forEach((record) => {
						const attr = attributeTemplates.find(
							(attr) => attr.attributeid === record.templateId,
						);
						if (!attr) return;

						const {name, type} = attr;
						// We will only store fields that are no writable as I would argue
						// there should be less of them than the others (if one is usign grid editor)
						if (!record.writable || readOnly) {
							!permissions[assetId]
								? (permissions[assetId] = [name])
								: permissions[assetId].push(name);
						}

						const values = parseValue(type, record.values);
						// @ts-ignore
						rowData[name] = values;
					});
					gridData.push(rowData);
				},
				() => {
					return {data: gridData, permissions};
				},
			);
		},
		[attributeSetSelection.attributeTemplateIds, progressStore.update],
	);

	const getSchema = useCallback(async () => {
		//const attributeIds = attributeSet.attributeIds;
		const schema: ColumnProps[] = [
			{
				name: 'assetId',
				header: i18n.t('i18n.assetId'),
				type: 'number',
				readOnly: true,
				id: 0,
			},
			{
				name: 'thumbUrl',
				header: i18n.t('i18n.thumbnail'),
				type: 'image',
				readOnly: true,
				id: 0,
			},
			{
				name: 'name',
				header: i18n.t('i18n.name'),
				type: 'text',
				readOnly: true,
				id: 0,
			},
		].concat(
			// TODO: make sure it is unique
			Object.values(attributeSetSelection.attributeTemplates)
				.flat()
				.filter((attr) => attr.attributeid > 0)
				.map((attr) => getColumnPropsForAttribute(attr)),
		);
		return schema;
	}, [attributeSetSelection.attributeTemplates]);

	const clone = useCallback(
		async (cloneFromId: number, cloneToIds: number[], fields: string[]) => {
			if (!gridEditor) return;

			const data = getDataFromGrid(gridEditor);
			const permissions = permissionRef.current;
			const cloneDataSource = data.find((d) => d.assetId === cloneFromId);
			if (!cloneDataSource) return;

			const totalItemsToCloneTo = cloneToIds.length;
			const columns = getColumnsFromGrid(gridEditor);
			const columnsByName = getColumnsByName(columns);

			const {round} = Math;

			const updatedData: FormValues[] = [];

			progressStore.update(0);

			// First build the data object that will be applied to each row
			const cloneFromData: FormValues = {};
			fields.forEach((field) => {
				const column = columnsByName[field];
				if (!column) return;

				const {parseInValue, readOnly, required} = column.properties;
				if (readOnly) return;

				const value = parseInValue
					? parseInValue(cloneDataSource[field])
					: cloneDataSource[field];

				if (!required || !isEmptyValue(value)) {
					cloneFromData[field] = value;
				}
			});

			// Build our updated data by row
			return breakItUp(
				{
					size: totalItemsToCloneTo,
				},
				(index) => {
					const assetId = cloneToIds[index];
					progressStore.update(
						round(((index + 1) / totalItemsToCloneTo) * 100),
					);
					const _cloneFromData = {...cloneFromData};
					if (permissions[assetId]) {
						permissions[assetId].forEach((field) => {
							delete _cloneFromData[field];
						});
					}
					updatedData.push({
						...cloneFromData,
						assetId,
					});
				},
				async () => {
					await updateSpecificValues(
						gridEditor,
						updatedData,
						dataRef.current,
						changeStoreRef.current,
						permissionRef.current,
						progressStore.update,
					);
					progressStore.update(100);
				},
			);
		},
		[gridEditor, progressStore.update],
	);

	const getCloneData = useCallback(
		(y: number) => {
			if (!gridEditor) return;

			const data = getDataFromGrid(gridEditor);

			const indexes = getSortIndexesFromGrid(gridEditor);
			const realIndex = indexes[y];

			const cloneData = data[realIndex];
			const cloneTargets = [...data];
			cloneTargets.splice(realIndex, 1);
			return {
				cloneData,
				cloneFields: getColumnsFromGrid(gridEditor),
				cloneSchema: schemaRef.current,
				cloneTargets,
			};
		},
		[gridEditor],
	);

	const workerAbortRef = useRef<() => void>();
	const getSelectedValues = useCallback(
		async (ignoreParseOut?: boolean) => {
			if (!gridEditor) return;

			if (workerAbortRef.current) {
				workerAbortRef.current();
				workerAbortRef.current = undefined;
			}

			const worker = _getSelectedValues(
				gridEditor,
				selectionStoreRef.current,
				permissionRef.current,
				progressStore.update,
				ignoreParseOut,
			);
			workerAbortRef.current = worker.abort;
			const data = await worker.job;
			if (!data) return;

			dispatch({
				type: ACTIONS.SET_STORED_DATA,
				data: data,
			});

			progressStore.update(100);
		},
		[gridEditor, progressStore.update],
	);

	const resetAllChanges = useCallback(async () => {
		if (!gridEditor) return;

		await resetAllValues(
			gridEditor,
			changeStoreRef.current,
			dataRef.current,
			progressStore.update,
		);
		progressStore.update(100);

		state.storedData && getSelectedValues(true);
	}, [gridEditor, getSelectedValues, state.storedData, progressStore.update]);

	const resetStore = useCallback(() => {
		if (selectionStoreRef.current.state.inSingleColumn === false) {
			if (workerAbortRef.current) {
				workerAbortRef.current();
				workerAbortRef.current = undefined;
			}

			dispatch({
				type: ACTIONS.CLEAR_STORED_DATA,
			});
			return;
		}
		getSelectedValues();
	}, [getSelectedValues]);

	const resetSelectedChanges = useCallback(async () => {
		if (!gridEditor) return;

		await resetSelectedValues(
			gridEditor,
			selectionStoreRef.current,
			changeStoreRef.current,
			dataRef.current,
			progressStore.update,
		);
		resetStore();
	}, [gridEditor, resetStore, state.storedData, progressStore.update]);

	const setData = useCallback(
		async (data: DataRow[]) => {
			if (!gridEditor) return;

			const schema = await getSchema();

			// Store
			dataRef.current = JSON.parse(JSON.stringify(data));
			schemaRef.current = JSON.parse(JSON.stringify(schema));
			// Reset
			selectionStoreRef.current.reset();
			changeStoreRef.current.reset();

			// Update grid
			onSetData(data, schema);

			setTimeout(() => gridEditor.resized(), 50);
		},
		[getSchema, gridEditor, onSetData],
	);

	// When attributeSet or loading state changes, update grid data
	useWhenChangedEffect(
		[attributeSetSelection.attributeTemplateIds, state.loading] as const,
		useCallback(
			([attributeIds, loadingData]) => {
				if (loadingData) return;
				if (!attributeIds.length) {
					permissionRef.current = {};
					setData(dataRef.current);
					return;
				}
				getGridData(rawDataRef.current).then((details) => {
					permissionRef.current = details.permissions;
					setData(details.data);
				});
			},
			[setData, state.loading],
		),
	);

	const updateData = useCallback(
		async (values: FormValues, actions?: FormActions) => {
			if (!gridEditor) return;

			await updateSelectedValues(
				gridEditor,
				selectionStoreRef.current,
				changeStoreRef.current,
				dataRef.current,
				values,
				permissionRef.current,
				actions,
				progressStore.update,
			);
			progressStore.update(100);

			resetStore();
		},
		[resetStore, progressStore.update],
	);

	// When gridEditor
	useEffect(() => {
		if (!gridEditor) return;

		let images: {
			[asset_id: string]: HTMLImageElement[];
		} = {};

		const cellConfig_setAlignment = (config: any, column: any) => {
			if (config.isHandleColumn) {
				config.halign = 'center';
				return;
			}

			if (column.name === 'assetId') {
				config.halign = 'center';
				return;
			}
			// Do it like excel/pages
			switch (column.getType()) {
				case 'date':
				case 'float':
				case 'int':
					config.halign = 'right';
					break;
				default:
					config.halign = 'left';
					break;
			}
		};

		const loadImage = (config: any) => {
			const data = getDataFromGrid(gridEditor);
			const sortIndex = getSortIndexesFromGrid(gridEditor);

			const dataCell = config.dataCell;
			const realIndex = sortIndex[dataCell.y];
			const rowData = data[realIndex];
			const primary = rowData.assetId;
			const maxThumbnailHeight = 150;
			const maxThumbnailWidth = 150;

			let rowImages = images[primary];
			if (!rowImages) {
				rowImages = images[primary] = [];
			}

			let img = rowImages[dataCell.x];

			// Already cached
			if (img) {
				config.value = img;
				return;
			}

			assetJwtStore
				.getState()
				.add([{assetId: rowData.assetId, jwt: rowData.jwt}]);

			// New image
			img = new Image();
			img.onload = () => {
				const ratio = img.naturalWidth / img.naturalHeight;
				let h = maxThumbnailHeight;
				let w = h * ratio;

				if (w > maxThumbnailWidth) {
					w = maxThumbnailWidth;
					h = w / ratio;
				}
				img.height = h;
				img.width = w;
				gridEditor.repaint();
			};
			img.src = config.value;

			// Cache and assign
			rowImages[dataCell.x] = config.value = img;
		};

		const cellConfig_setColor = (config: any, column: any) => {
			if (!config.isUserDataArea) return;
			const data = getDataFromGrid(gridEditor);
			const sortIndex = getSortIndexesFromGrid(gridEditor);
			const rowData = data[sortIndex[config.dataCell.y]];
			const columnProps = column.properties;
			const perms = permissionRef.current[rowData.assetId];

			const {properties} = gridEditor;
			// Uneditable cell
			if (
				column.index < properties.fixedColumnCount ||
				(perms && perms.includes(column.name))
			) {
				config.backgroundColor = properties.cellReadOnlyColor;
			} else if (changeStoreRef.current.changed(rowData.assetId, column.name)) {
				config.backgroundColor = properties.cellChangedColor;
			}
			// Mandatory cell
			else if (columnProps.required) {
				config.backgroundColor = properties.cellMandatoryColor;
			}
			// Default background color
			else {
				config.backgroundColor = properties.cellDefaultColor;
			}
		};

		const getCellRenderer = (config: any, declaredRendererName: any) => {
			const point = config.dataCell;

			const column = gridEditor.behavior.getColumn(point.x);

			cellConfig_setAlignment(config, column);
			cellConfig_setColor(config, column);

			// Not a data cell
			if (!config.isUserDataArea) {
				// No selection checkboxes at all
				if (gridEditor.properties.rowSelection) {
					if (point.x === -2) {
						config.centerIcon = config.leftIcon = null;
						// config.bounds.width = 40;
					}
				}
				// No checkboxes on the data rows (maintain the select all one in the top left corner)
				else if (config.isHandleColumn) {
					// Header - center the icon
					if (config.gridCell.y === 0) {
						config.centerIcon = config.leftIcon;
					}
					config.leftIcon = null;
					// config.bounds.width = 40;
				}
				return gridEditor.cellRenderers.get(declaredRendererName);
			}

			// This is my own deal that I will be submitting as a pull request when they merge the refactor branch into master
			config.multiline = true;

			// Check for schema formatting
			if (column.properties.formatValue) {
				config.value = column.properties.formatValue(config.value);
			}

			if (column.type === 'image') {
				// Value should now be an image url
				loadImage(config);
			}

			// Switch over field name
			switch (column.getType()) {
				case 'image':
					return gridEditor.cellRenderers.get('ThumbnailCell');
				default:
					return gridEditor.cellRenderers.get(declaredRendererName);
			}
		};

		gridEditor.behavior.dataModel.getCell = getCellRenderer;
		gridEditor.behavior.dataModel.getCellEditorAt = getCellEditor(
			gridEditor,
			permissionRef,
		);

		const handleCellClicked = (event: HypergridCellEvent) => {
			const {detail} = event;

			let point = detail.gridCell;
			if (!point || !gridEditor.isGridRow(point.y)) return;

			point = getDataPointFromCellEvent(event);

			const indexes = getSortIndexesFromGrid(gridEditor);
			const realIndex = indexes[point.y];
			const rowData = getDataFromGrid(gridEditor)[realIndex];
			if (!rowData) return;

			const column = getColumnFromGrid(gridEditor, point.x);
			if (column.name === 'thumbUrl') {
				dispatcher.trigger('asset:tagPanel:grid:preview', rowData.assetId);
			}
		};

		const handleCellEditorDataChange = (event: HypergridCellEvent) => {
			const {detail} = event;
			const {input} = detail;
			const {name} = input.column;

			const data = getDataFromGrid(gridEditor);
			const sortIndex = getSortIndexesFromGrid(gridEditor);

			const realIndex = sortIndex[input.event.dataCell.y];
			const rowData = data[realIndex];

			const {assetId} = rowData;
			const originalValue = dataRef.current.find(
				(data) => data.assetId === assetId,
			)?.[name];

			!checkEquality(detail.newValue, originalValue)
				? changeStoreRef.current.add(assetId, name)
				: changeStoreRef.current.remove(assetId, name);

			resetStore();

			gridEditor.repaint();
			setTimeout(() => {
				gridEditor.takeFocus();
			}, 51);
		};

		const handleResize = () => {
			// DAM-18842
			const {sbHScroller, sbVScroller} = gridEditor;
			setTimeout(() => {
				if (sbHScroller) {
					dispatch({
						type: ACTIONS.SET_OVERFLOW_X,
						overflown: sbHScroller.contentSize > sbHScroller.containerSize,
					});
				}
				if (sbVScroller) {
					dispatch({
						type: ACTIONS.SET_OVERFLOW_Y,
						overflown: sbVScroller.contentSize > sbVScroller.containerSize,
					});
				}
			}, 51);
		};

		gridEditor.addEventListener('fin-click', handleCellClicked);
		gridEditor.addEventListener(
			'fin-editor-data-change',
			handleCellEditorDataChange,
		);
		gridEditor.addEventListener('fin-grid-resized', handleResize);

		return () => {
			const assetIds = Object.keys(images).map((id) => parseInt(id));
			assetJwtStore.getState().remove(assetIds);
			images = {};
			gridEditor.removeEventListener('fin-click', handleCellClicked);
			gridEditor.removeEventListener(
				'fin-editor-data-change',
				handleCellEditorDataChange,
			);
			gridEditor.removeEventListener('fin-grid-resized', handleResize);
		};
	}, [gridEditor, getSelectedValues, resetStore]);

	// When selection updates
	useEffect(() => {
		resetStore();
	}, [selectionStore.state.cells, selectionStore.state.inSingleColumn]);

	// Listen for clone
	useEffect(() => {
		if (!gridEditor) return;

		const listener = (assetIds: number[], fields: string[]) => {
			const {inSingleRow} = selectionStoreRef.current.state;
			if (inSingleRow === false) return;

			const indexes = getSortIndexesFromGrid(gridEditor);
			const realIndex = indexes[inSingleRow];

			const data = getDataFromGrid(gridEditor);
			const cloneData = data[realIndex];
			clone(cloneData.assetId, assetIds, fields);
		};
		dispatcher.on('asset:tagPanel:grid:clone:submit:confirmed', listener);
		return () => {
			dispatcher.off('asset:tagPanel:grid:clone:submit:confirmed', listener);
		};
	}, [gridEditor, clone]);

	// When cart updates
	useWhenChangedEffect(
		[cart.assetCount, cart.hasAssets],
		useCallback(
			([assetCount, hasAssets]) => {
				if (!gridEditor || !hasAssets) return;

				progressStore.update(0);
				dispatch({
					type: ACTIONS.SET_LOADING,
					loading: true,
				});

				setTimeout(async () => {
					const data = await getData();
					rawDataRef.current = data;
					const gridDetails = await getGridData(data);
					dataRef.current = JSON.parse(JSON.stringify(gridDetails.data));
					permissionRef.current = gridDetails.permissions;

					dispatch({
						type: ACTIONS.SET_LOADING,
						loading: false,
					});
				}, 500);

				return () => {
					dataRef.current = [];
					permissionRef.current = {};
				};
			},
			[gridEditor, progressStore.update, setData, getGridData],
		),
		{
			executeAtMount: true,
		},
	);

	const save = useCallback(async () => {
		if (!gridEditor) return;

		const data = getDataFromGrid(gridEditor);
		const columns = getColumnsFromGrid(gridEditor);
		const columnsByName = getColumnsByName(columns);

		if (workerAbortRef.current) {
			workerAbortRef.current();
			workerAbortRef.current = undefined;
		}

		dispatch({
			type: ACTIONS.SET_SAVING,
			saving: true,
		});

		setTimeout(async () => {
			const updatedData: SetAssetAttributesSchema[] = [];
			const changes = changeStoreRef.current.state.changes;

			const assetIds = Object.keys(changes) as string[];
			const size = assetIds.length;

			await breakItUp(
				{
					size,
				},
				(index) => {
					const assetId = parseInt(assetIds[index] as any);
					const attributes: SetAssetAttributesAttribute[] = [];

					const rowData = data.find((d) => d.assetId === assetId);
					if (!rowData) return;

					changes[assetId].forEach((field) => {
						const column = columnsByName[field];
						if (!column) return;

						const {properties} = column;
						const value = properties.parseOutValue
							? properties.parseOutValue(rowData[field])
							: rowData[field];

						attributes.push({
							assetId,
							templateId: properties.id,
							values: Array.isArray(value) ? value : [value],
						});
					});

					attributes.length &&
						updatedData.push({
							assetId,
							attributes,
						});
				},
			);

			await saveData(updatedData, progressStore);
			dispatcher.trigger('asset:editor:grid:edited');

			// Reflect on our stored raw data
			const updatedDataSize = updatedData.length;
			await breakItUp(
				{
					size: updatedDataSize,
				},
				(index) => {
					const item = updatedData[index];
					const rawData = rawDataRef.current.find(
						(d) => d.assetId === item.assetId,
					);
					if (!rawData) return;
					item.attributes.forEach((attr) => {
						const rawAttr = rawData.attributes.find(
							(rawAttr) => rawAttr.templateId === attr.templateId,
						);
						if (!rawAttr) return;
						rawAttr.values = attr.values;
					});
				},
			);
			// Store
			dataRef.current = JSON.parse(JSON.stringify(data));
			// Reset
			selectionStoreRef.current.reset();
			changeStoreRef.current.reset();

			dispatch({
				type: ACTIONS.SET_SAVING,
				saving: false,
			});
		}, 100);
	}, [gridEditor, progressStore.update]);

	return (
		<StoreContext.Provider
			value={{
				state,
				getCloneData,
				getSelectedValues,
				resetAllChanges,
				resetSelectedChanges,
				resetStore,
				save,
				setData,
				updateData,
			}}
		>
			{children}
		</StoreContext.Provider>
	);
};
